"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import BookCtaView from "@/components/optilase/home/book-cta-view";
import { mergeSourced, type SourcedBlok } from "@/lib/sourced-blok";
import { regionFromPathname } from "@/lib/region";
import { surfaceFrom } from "@/components/optilase/surface";
import { buttonVariant } from "@/lib/button-color";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { usePublicPathname } from "@/lib/public-pathname";

type CtaCalloutFields = {
  heading?: string;
  accent_heading?: string;
  description?: string;
  features?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  button_action?: string;
  button_color?: string;
  bg_color?: string;
  panel_color?: string;
  heading_size?: string;
};

type CtaCalloutBlok = SbBlokData & SourcedBlok<CtaCalloutFields>;

const FIELDS: (keyof CtaCalloutFields)[] = [
  "heading",
  "accent_heading",
  "description",
  "features",
  "button_label",
  "button_link",
  "button_action",
  "button_color",
  "bg_color",
  "panel_color",
  "heading_size",
];

export default function CtaCallout({ blok }: { blok: CtaCalloutBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const merged = mergeSourced(blok, FIELDS);
  const link = resolveLink(merged.button_link, region);

  const points = (merged.features ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  return (
    <div {...storyblokEditable(blok)}>
      <BookCtaView
        surface={surfaceFrom(merged.bg_color)}
        panel={surfaceFrom(merged.panel_color)}
        headingSize={merged.heading_size === "small" ? "small" : "large"}
        heading={merged.heading ?? ""}
        headingAccent={merged.accent_heading}
        points={points}
        ctaLabel={merged.button_label ?? ""}
        ctaHref={link.href}
        ctaAction={merged.button_action}
        ctaVariant={buttonVariant(merged.button_color, "white")}
      />
    </div>
  );
}
